import { plain } from '../../lib/labels';
import { useState } from 'react';
import {
  Alert,
  App,
  Button,
  Card,
  Descriptions,
  Drawer,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
  type TableColumnsType,
} from 'antd';
import { ReloadOutlined, StopOutlined, VideoCameraOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { PersonName, listenerText, userLabel, userSecondary, validDisplayName } from '../../lib/listenerName';
import { Can } from '../../rbac/RequirePermission';

type LiveStatus = 'live' | 'paused' | 'ended' | 'ended_flagged' | 'scheduled';

interface LiveRow {
  id: string;
  title: string;
  category: string | null;
  status: LiveStatus;
  /** displayName = listener name (when a listener); userDisplayName = the account's own name. */
  streamer: { id: string; handle: string; displayName?: string | null; userDisplayName?: string | null };
  viewerCount: number;
  peakViewerCount: number;
  totalViewers: number;
  startedAt: string;
  endedAt: string | null;
  durationSeconds: number | null;
  endedReason: string | null;
  rtcProvider: string;
}

interface LiveLog {
  id: string;
  liveStreamId: string;
  streamerUserId: string;
  eventType: string;
  adminId: string | null;
  reason: string | null;
  createdAt: string;
}

interface LiveSettings {
  enabled: boolean;
  commentMaxLength: number;
  commentRatePerMinute: number;
  reapAfterMinutes: number;
  nearbyRadiusKm: number;
  maxViewers: number;
  titleMaxLength: number;
  /** Absent on an older API build. */
  requireOnline?: boolean;
}

const STATUS_COLOUR: Record<LiveStatus, string> = {
  live: 'red',
  paused: 'orange',
  ended: 'default',
  ended_flagged: 'volcano',
  scheduled: 'blue',
};

/**
 * A timestamp the API may not have sent.
 *
 * `new Date(v).toLocaleString()` is not safe on live data: `new Date(null)` is
 * the epoch, so a stream with no `startedAt` rendered a confident
 * "1/1/1970, 5:30:00 am", and `new Date(undefined)` renders the string
 * "Invalid Date". Both are stale/fake Live data presented as real, which is
 * exactly what an ops console must not do.
 */
function when(v: string | null | undefined): string {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

function duration(seconds: number | null): string {
  if (seconds === null) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

/** Streams currently on air, with force-terminate. */
function StreamsTab() {
  const qc = useQueryClient();
  const { message, modal } = App.useApp();
  const [status, setStatus] = useState<LiveStatus>('live');
  const [detailId, setDetailId] = useState<string | null>(null);

  const { data, isFetching, refetch } = useQuery({
    queryKey: ['live', 'streams', status],
    queryFn: async () =>
      (await api.get<{ items: LiveRow[] }>('/admin/live/streams', { params: { status } })).data,
    // Streams are a live surface — a stale list hides a room that needs stopping.
    refetchInterval: 10_000,
  });

  const { data: detail } = useQuery({
    queryKey: ['live', 'detail', detailId],
    queryFn: async () => (await api.get(`/admin/live/${detailId}`)).data as {
      stream: (LiveRow & { agoraChannel: string; pausedForCallId: string | null }) | null;
      // Optional on purpose. The declared type used to promise these were
      // always present, so `detail.audience.length` type-checked — and then
      // threw "Cannot read properties of undefined" on an ended or reaped
      // session, which unmounts the React tree and whites out the console.
      // A type is a claim about the server, and this one was wrong.
      liveViewerCount?: number;
      audience?: Array<{ userId: string; handle: string; displayName?: string | null; joinedAt: string }>;
    },
    enabled: !!detailId,
  });

  const forceEnd = useMutation({
    mutationFn: async (vars: { id: string; reason: string }) =>
      api.post(`/admin/live/${vars.id}/force-end`, { reason: vars.reason }),
    onSuccess: () => {
      message.success('Stream terminated');
      void qc.invalidateQueries({ queryKey: ['live'] });
      setDetailId(null);
    },
    onError: () => message.error('Could not terminate the stream'),
  });

  function confirmTerminate(row: LiveRow) {
    let reason = '';
    modal.confirm({
      title: `Terminate "${row.title || 'Untitled'}"?`,
      icon: <StopOutlined style={{ color: '#cf1322' }} />,
      content: (
        <div>
          <p style={{ marginTop: 8 }}>
            The audience is told the stream was terminated. This is recorded against your
            admin account.
          </p>
          <Input.TextArea
            rows={2}
            placeholder="Reason (required, min 3 characters)"
            onChange={(e) => {
              reason = e.target.value;
            }}
          />
        </div>
      ),
      okText: 'Terminate',
      okButtonProps: { danger: true },
      onOk: () => {
        if (reason.trim().length < 3) {
          message.error('A reason is required');
          return Promise.reject(new Error('reason required'));
        }
        return forceEnd.mutateAsync({ id: row.id, reason: reason.trim() });
      },
    });
  }

  const columns: TableColumnsType<LiveRow> = [
    {
      title: 'Stream',
      dataIndex: 'title',
      render: (t: string, row) => (
        <Space direction="vertical" size={0}>
          <Typography.Link onClick={() => setDetailId(row.id)}>{t || 'Untitled'}</Typography.Link>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {listenerText({
              displayName: validDisplayName(row.streamer.displayName) ?? row.streamer.userDisplayName,
              handle: row.streamer.handle,
            })}
            {row.category ? ` · ${row.category}` : ''}
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 130,
      render: (s: LiveStatus, row) => (
        <Space direction="vertical" size={0}>
          <Tag color={STATUS_COLOUR[s]}>{plain(s)}</Tag>
          {row.endedReason ? (
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              {row.endedReason}
            </Typography.Text>
          ) : null}
        </Space>
      ),
    },
    {
      title: 'Watching',
      dataIndex: 'viewerCount',
      width: 100,
      align: 'right',
      render: (v: number, row) => (
        <Space direction="vertical" size={0} style={{ textAlign: 'right' }}>
          <b>{v}</b>
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            peak {row.peakViewerCount} · {row.totalViewers} total
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: 'Started',
      dataIndex: 'startedAt',
      width: 170,
      render: (v: string, row) => (
        <Space direction="vertical" size={0}>
          <span>{when(v)}</span>
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            {duration(row.durationSeconds)}
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: '',
      width: 120,
      render: (_: unknown, row) =>
        row.status === 'live' || row.status === 'paused' ? (
          <Can permission="live.force_end">
            <Button danger size="small" icon={<StopOutlined />} onClick={() => confirmTerminate(row)}>
              Terminate
            </Button>
          </Can>
        ) : null,
    },
  ];

  return (
    <>
      <Space style={{ marginBottom: 12 }}>
        <Select
          value={status}
          style={{ width: 170 }}
          onChange={(v: LiveStatus) => setStatus(v)}
          options={[
            { value: 'live', label: 'On air' },
            { value: 'paused', label: 'Paused (in a call)' },
            { value: 'ended', label: 'Ended' },
            { value: 'ended_flagged', label: 'Terminated' },
          ]}
        />
        <Button icon={<ReloadOutlined />} onClick={() => void refetch()} loading={isFetching}>
          Refresh
        </Button>
      </Space>

      <Table<LiveRow>
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={data?.items ?? []}
        loading={isFetching && !data}
        pagination={false}
      />

      <Drawer
        width={560}
        open={!!detailId}
        onClose={() => setDetailId(null)}
        title={detail?.stream?.title || 'Stream'}
      >
        {detail?.stream ? (
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="Status">
                <Tag color={STATUS_COLOUR[detail.stream.status]}>{plain(detail.stream.status)}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Streamer">
                <PersonName
                  displayName={detail.stream.streamer?.displayName}
                  userDisplayName={detail.stream.streamer?.userDisplayName}
                  handle={detail.stream.streamer?.handle}
                  userId={detail.stream.streamer?.id}
                />
              </Descriptions.Item>
              <Descriptions.Item label="Watching now">{detail.liveViewerCount ?? 0}</Descriptions.Item>
              <Descriptions.Item label="Peak / total">
                {detail.stream.peakViewerCount} / {detail.stream.totalViewers}
              </Descriptions.Item>
              <Descriptions.Item label="Started">
                {when(detail.stream.startedAt)}
              </Descriptions.Item>
              <Descriptions.Item label="Duration">
                {duration(detail.stream.durationSeconds)}
              </Descriptions.Item>
              <Descriptions.Item label="Media">
                {detail.stream.rtcProvider} · <code>{detail.stream.agoraChannel}</code>
              </Descriptions.Item>
              {detail.stream.pausedForCallId ? (
                <Descriptions.Item label="Paused for call">
                  <code>{detail.stream.pausedForCallId}</code>
                </Descriptions.Item>
              ) : null}
            </Descriptions>

            <Card size="small" title={`Audience (${(detail.audience ?? []).length})`}>
              {(detail.audience ?? []).length === 0 ? (
                <Typography.Text type="secondary">Nobody watching.</Typography.Text>
              ) : (
                <Space wrap>
                  {(detail.audience ?? []).map((a) => (
                    <Tag key={a.userId}>{[userLabel(a), userSecondary(a)].filter(Boolean).join(' · ')}</Tag>
                  ))}
                </Space>
              )}
            </Card>
          </Space>
        ) : null}
      </Drawer>
    </>
  );
}

/** The lifecycle audit trail — who terminated what, and what the reaper closed. */
function LogsTab() {
  const { data, isFetching, refetch } = useQuery({
    queryKey: ['live', 'logs'],
    queryFn: async () => (await api.get<{ items: LiveLog[] }>('/admin/live/logs')).data,
  });

  const columns: TableColumnsType<LiveLog> = [
    {
      title: 'When',
      dataIndex: 'createdAt',
      width: 180,
      render: (v: string | null) => when(v),
    },
    {
      title: 'Event',
      dataIndex: 'eventType',
      width: 170,
      render: (v: string) => (
        <Tag color={v === 'force_ended' ? 'volcano' : v === 'reaped' ? 'orange' : 'default'}>{plain(v)}</Tag>
      ),
    },
    {
      title: 'Stream',
      dataIndex: 'liveStreamId',
      // Guarded the same way the `By` column below already is: a log row whose
      // stream has since been hard-deleted arrives with a null id, and
      // `null.slice` took the whole Logs tab down.
      render: (v: string | null) =>
        v ? <code>{v.slice(0, 8)}…</code> : <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: 'By',
      dataIndex: 'adminId',
      render: (v: string | null) =>
        v ? <code>{v.slice(0, 8)}…</code> : <Typography.Text type="secondary">system</Typography.Text>,
    },
    { title: 'Reason', dataIndex: 'reason', render: (v: string | null) => v ?? '—' },
  ];

  return (
    <>
      <Button
        icon={<ReloadOutlined />}
        onClick={() => void refetch()}
        loading={isFetching}
        style={{ marginBottom: 12 }}
      >
        Refresh
      </Button>
      <Table<LiveLog>
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={data?.items ?? []}
        loading={isFetching && !data}
        pagination={{ pageSize: 25 }}
      />
    </>
  );
}

function SettingsTab() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const { data } = useQuery({
    queryKey: ['live', 'settings'],
    queryFn: async () => (await api.get<LiveSettings>('/admin/live/settings')).data,
  });
  const [draft, setDraft] = useState<Partial<LiveSettings>>({});

  const save = useMutation({
    mutationFn: async (body: Partial<LiveSettings>) => api.post('/admin/live/settings', body),
    onSuccess: () => {
      message.success('Saved');
      setDraft({});
      void qc.invalidateQueries({ queryKey: ['live', 'settings'] });
    },
    onError: () => message.error('Could not save'),
  });

  if (!data) return null;
  const v = { ...data, ...draft };

  return (
    <Card style={{ maxWidth: 640 }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Space>
          <Switch
            // GET returns `enabled`, POST takes `liveEnabled`: show the pending edit, else the saved value.
            checked={(draft as { liveEnabled?: boolean }).liveEnabled ?? data.enabled}
            onChange={(c) => setDraft((d) => ({ ...d, liveEnabled: c }))}
          />
          <span>Live enabled — off stops any NEW stream starting; running streams are unaffected.</span>
        </Space>

        {data.requireOnline !== undefined && (
          <Space>
            <Switch
              checked={v.requireOnline}
              onChange={(c) => setDraft((d) => ({ ...d, requireOnline: c }))}
            />
            <span>
              Host must be Online — a listener can start a Live only while Online. Checked at start;
              a running Live is not ended if they go Offline.
            </span>
          </Space>
        )}

        <Space direction="vertical">
          <span>Comment max length</span>
          <InputNumber
            min={1}
            max={1000}
            value={v.commentMaxLength}
            onChange={(n) => setDraft((d) => ({ ...d, commentMaxLength: n ?? undefined }))}
          />
        </Space>

        <Space direction="vertical">
          <span>Comments per minute, per viewer, per stream</span>
          <InputNumber
            min={1}
            max={600}
            value={v.commentRatePerMinute}
            onChange={(n) => setDraft((d) => ({ ...d, commentRatePerMinute: n ?? undefined }))}
          />
        </Space>

        <Space direction="vertical">
          <span>Nearby radius (km)</span>
          <InputNumber
            min={1}
            max={500}
            value={v.nearbyRadiusKm}
            onChange={(n) => setDraft((d) => ({ ...d, nearbyRadiusKm: n ?? undefined }))}
          />
        </Space>

        <Space direction="vertical">
          <span>Max viewers per stream (0 = unlimited)</span>
          <InputNumber
            min={0}
            value={v.maxViewers}
            onChange={(n) => setDraft((d) => ({ ...d, maxViewers: n ?? undefined }))}
          />
        </Space>

        <Space direction="vertical" style={{ width: '100%' }}>
          <span>Reap abandoned streams after (minutes)</span>
          <InputNumber
            min={360}
            max={10080}
            value={v.reapAfterMinutes}
            onChange={(n) => setDraft((d) => ({ ...d, reapAfterMinutes: n ?? undefined }))}
          />
          <Alert
            type="warning"
            showIcon
            message="Cannot go below 360 minutes"
            description={
              'The reaper reads the session lock to decide whether a stream is abandoned, and that lock ' +
              'lives 6 hours. Redis is volatile: a restart loses every lock at once, and reaping below the ' +
              'lock lifetime would read that as "every broadcaster vanished" and end every live stream on ' +
              'the platform.'
            }
          />
        </Space>

        <Can permission="live.settings.edit">
          <Button
            type="primary"
            disabled={Object.keys(draft).length === 0}
            loading={save.isPending}
            onClick={() => save.mutate(draft)}
          >
            Save
          </Button>
        </Can>
      </Space>
    </Card>
  );
}

export function LivePage() {
  const { data: stats } = useQuery({
    queryKey: ['live', 'stats'],
    queryFn: async () =>
      (await api.get<{ liveNow: number; paused: number }>('/admin/live/stats')).data,
    refetchInterval: 15_000,
  });

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Space align="center">
        <VideoCameraOutlined style={{ fontSize: 20 }} />
        <Typography.Title level={4} style={{ margin: 0 }}>
          Live streams
        </Typography.Title>
        {stats ? (
          <Space>
            <Tag color="red">{stats.liveNow} on air</Tag>
            {stats.paused > 0 ? <Tag color="orange">{stats.paused} paused in a call</Tag> : null}
          </Space>
        ) : null}
      </Space>

      <Tabs
        items={[
          { key: 'streams', label: 'Streams', children: <StreamsTab /> },
          { key: 'logs', label: 'Audit log', children: <LogsTab /> },
          { key: 'settings', label: 'Settings', children: <SettingsTab /> },
        ]}
      />
    </Space>
  );
}
